// Invitation offers as the invitee sees them (`my_invitations`, `preview_invite_code` → `invitation_offer_json`,
// supabase/migrations/20260929180000_groundteam_self_signup.sql:279) and the wording NoCompanyView builds from one
// (InvitationOfferSummary, joinDetail, confirmTitle in GroundTeam/Views/Sync/NoCompanyView.swift), with "this iPhone"
// said for the browser. Decoding is tolerant like `SupabaseSyncAPI.offer`: an entry without a UUID id and company id is
// skipped, an unknown role reads as the least privileged one, and a missing date never drops the invitation.
// Pure, so the wording is unit-tested.

import { ROLE_TITLES } from '@/auth/accountText';
import { isUuid } from '@/contracts/uuid';
import type { InvitationOffer, Role } from '@/contracts/wire';
import { parseWireDate } from '@/contracts/wireDate';
import { formatShortDate } from '@/domain/format';
import { GT_COLORS } from '@/ui/color';

const ROLES: readonly Role[] = ['admin', 'manager', 'rep'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `SupabaseSyncAPI.value`: the first present, non-null value among `keys`. */
function firstValue(d: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const v = d[key];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

function text(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function nonEmpty(value: unknown): string | null {
  const t = text(value)?.trim();
  return t ? t : null;
}

/** One `invitation_offer_json`; null when it has no usable id or company id. */
export function parseInvitationOffer(value: unknown): InvitationOffer | null {
  if (!isRecord(value)) return null;
  const id = text(firstValue(value, 'id', 'invitation_id', 'invitationId'))?.trim() ?? '';
  const companyId = text(firstValue(value, 'company_id', 'companyId'))?.trim() ?? '';
  if (!isUuid(id) || !isUuid(companyId)) return null;
  const rawRole = typeof value.role === 'string' ? value.role.toLowerCase() : '';
  return {
    id,
    company_id: companyId,
    company_name: text(firstValue(value, 'company_name', 'companyName')),
    role: (ROLES as readonly string[]).includes(rawRole) ? (rawRole as Role) : 'rep',
    invited_by: nonEmpty(firstValue(value, 'invited_by', 'invitedBy')),
    created_at: text(firstValue(value, 'created_at', 'createdAt')) ?? '',
    expires_at: text(firstValue(value, 'expires_at', 'expiresAt')),
  };
}

/** `my_invitations`: an array of offers (anything else reads as none). */
export function parseInvitationOffers(json: unknown): InvitationOffer[] {
  if (!Array.isArray(json)) return [];
  const out: InvitationOffer[] = [];
  for (const entry of json) {
    const offer = parseInvitationOffer(entry);
    if (offer) out.push(offer);
  }
  return out;
}

/** `preview_invite_code`: one offer, or the first usable one if the server ever answers with a list. */
export function parsePreviewOffer(json: unknown): InvitationOffer | null {
  return parseInvitationOffer(json) ?? parseInvitationOffers(json)[0] ?? null;
}

// MARK: - Wording

/** `InvitationOffer.displayCompany`: the company's name, or "this company" when the server has none. */
export function offerCompany(offer: Pick<InvitationOffer, 'company_name'>): string {
  const name = offer.company_name?.trim();
  return name ? name : 'this company';
}

/** Who sent it (email or phone number); null when the inviter's account no longer exists. */
export function offerInviter(offer: Pick<InvitationOffer, 'invited_by'>): string | null {
  const by = offer.invited_by?.trim();
  return by ? by : null;
}

/** Role chip colors (`teamRoleColor`): admin orange (filled), manager purple, rep blue. */
export const ROLE_COLORS: Readonly<Record<Role, string>> = {
  admin: GT_COLORS.orange,
  manager: GT_COLORS.purple,
  rep: GT_COLORS.blue,
};

export function roleTitle(role: Role): string {
  return ROLE_TITLES[role];
}

/** `UserRole.titleWithArticle`: "a manager", "an admin", "a sales rep" (for sentences). */
export function roleWithArticle(role: Role): string {
  const noun = ROLE_TITLES[role].toLowerCase();
  return ('aeiou'.includes(noun.charAt(0)) ? 'an ' : 'a ') + noun;
}

function createdDate(offer: Pick<InvitationOffer, 'created_at'>, now: Date): Date {
  // iOS falls back to "now" for a missing creation date.
  return parseWireDate(offer.created_at) ?? now;
}

/** "Invited by ana@acme.com", or "Inviter unknown · invited Sep 22, 2026". */
export function invitedLine(
  offer: Pick<InvitationOffer, 'invited_by' | 'created_at'>,
  locale?: string,
  now: Date = new Date(),
): string {
  const by = offerInviter(offer);
  if (by) return `Invited by ${by}`;
  return `Inviter unknown · invited ${formatShortDate(createdDate(offer, now), locale)}`;
}

/** "Expires Oct 6, 2026"; null when the invitation has no expiry date. */
export function expiresLine(offer: Pick<InvitationOffer, 'expires_at'>, locale?: string): string | null {
  const at = parseWireDate(offer.expires_at);
  return at ? `Expires ${formatShortDate(at, locale)}` : null;
}

/** What a screen reader hears for one offer (InvitationOfferSummary's combined accessibility label). */
export function offerSummaryLabel(offer: InvitationOffer, locale?: string, now: Date = new Date()): string {
  const expires = expiresLine(offer, locale);
  return `${offerCompany(offer)}, ${roleTitle(offer.role)}. ${invitedLine(offer, locale, now)}${expires ? `. ${expires}` : ''}`;
}

/** The join confirmation's title: "Join Acme Roofing as a manager?". */
export function joinConfirmTitle(offer: Pick<InvitationOffer, 'company_name' | 'role'>): string {
  return `Join ${offerCompany(offer)} as ${roleWithArticle(offer.role)}?`;
}

/**
 * Who invited them, the role, and what opens after joining (`joinDetail(for:)`). The browser holds no company data of
 * its own here, so it is the iOS "fresh install" sentence, with the console's word for records.
 */
export function joinDetail(offer: Pick<InvitationOffer, 'company_name' | 'role' | 'invited_by'>): string {
  const company = offerCompany(offer);
  const by = offerInviter(offer);
  const invited = by ? `Invited by ${by}. ` : 'Inviter unknown — join only if you expected this invitation. ';
  const role = `You join as ${roleWithArticle(offer.role)}; the server enforces what that role can see and change.`;
  return `${invited}${role} ${company}'s stores, activities, territories, routes, templates and settings load in this browser.`;
}
